import React from 'react';
import { AlertTriangle, CheckCircle, HelpCircle, Clock, UserX, ChevronUp, ChevronDown, ChevronsUpDown, ClipboardPaste, Ban } from 'lucide-react';

function UserList({ users, selectedUsers, onUserSelectionChange, onSelectAll, requestSort, sortConfig, onUseStoredSignature, onSuspendUser }) {
  if (!users) {
    return null; // Ne rien afficher si users est undefined (pendant le chargement initial)
  }
  if (users.length === 0) {
    return <p className="text-center text-gray-500 py-4">Aucun utilisateur à afficher.</p>;
  }

  const handleSelectAllClick = () => {
    const areAllCurrentlySelected = selectedUsers.size === users.length;
    onSelectAll(areAllCurrentlySelected);
  };

  const getStatusIconAndColor = (statusDetails, signatureConfigured) => {
    if (signatureConfigured) {
      return { icon: <CheckCircle size={16} className="text-green-500" />, color: 'text-green-600', text: 'Configurée' };
    }
    switch (statusDetails?.status) {
      case 'pending_login':
        return { icon: <Clock size={16} className="text-blue-500" />, color: 'text-blue-600', text: 'En attente (connexion)' };
      case 'batch_update_initiated':
        return { icon: <Clock size={16} className="text-purple-500" />, color: 'text-purple-600', text: 'MàJ par lot initiée' };
      case 'configuration_failed':
      case 'batch_failed':
      case 'credential_error':
      case 'admin_config_error':
        return { icon: <AlertTriangle size={16} className="text-red-500" />, color: 'text-red-600', text: 'Échec configuration' };
      default:
        return { icon: <HelpCircle size={16} className="text-gray-400" />, color: 'text-gray-500', text: 'Inconnu/Non géré' };
    }
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown size={14} className="ml-1 text-gray-400 group-hover:text-gray-600" />;
    }
    if (sortConfig.direction === 'ascending') {
      return <ChevronUp size={14} className="ml-1 text-indigo-600" />;
    }
    return <ChevronDown size={14} className="ml-1 text-indigo-600" />;
  };

  const thClasses = "py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 group cursor-pointer hover:bg-gray-50";
  const thSortableClasses = (columnKey) => 
    `${thClasses} ${sortConfig.key === columnKey ? 'bg-indigo-50' : ''}`;

  return (
    <div className="mt-8 flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="relative">
            {selectedUsers.size > 0 && (
              <div className="absolute left-14 top-0 flex h-12 items-center space-x-3 bg-white sm:left-12">
                <button
                  type="button"
                  className="inline-flex items-center rounded bg-red-50 px-2 py-1 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-red-200 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                  onClick={() => onSelectAll(true)} // true pour désélectionner tout
                >
                  Désélectionner ({selectedUsers.size})
                </button>
              </div>
            )}
            <table className="min-w-full table-fixed divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" className="relative px-7 sm:w-12 sm:px-6">
                    <input
                      type="checkbox"
                      className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={users.length > 0 && selectedUsers.size === users.length}
                      onChange={handleSelectAllClick}
                      disabled={users.length === 0}
                    />
                  </th>
                  <th scope="col" className={thSortableClasses('name')} onClick={() => requestSort('name')}>
                    <div className="flex items-center">
                      Utilisateur (Email / Nom)
                      {renderSortIcon('name')}
                    </div>
                  </th>
                  <th scope="col" className={`${thSortableClasses('statusDetails')} px-3`} onClick={() => requestSort('statusDetails')}>
                     <div className="flex items-center">
                       Statut Signature
                       {renderSortIcon('statusDetails')}
                     </div>
                  </th>
                  <th scope="col" className={`${thSortableClasses('managedByApp')} px-3`} onClick={() => requestSort('managedByApp')}>
                     <div className="flex items-center">
                       Géré via App
                       {renderSortIcon('managedByApp')}
                     </div>
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Détails / Infos
                  </th>
                  <th scope="col" className={`${thSortableClasses('suspended')} px-3`} onClick={() => requestSort('suspended')}>
                     <div className="flex items-center">
                       Compte Google
                       {renderSortIcon('suspended')}
                     </div>
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {users.map(user => {
                  const { icon, color, text: statusText } = getStatusIconAndColor(user.statusDetails, user.signatureConfigured);
                  const isSelected = selectedUsers.has(user.email);
                  console.log('User for action button:', user.email, 'HasHTML:', !!user.generatedSignatureHtml, 'HTML:', user.generatedSignatureHtml, 'Configured:', user.signatureConfigured);
                  return (
                    <tr key={user.email} className={isSelected ? 'bg-indigo-50' : undefined}>
                      <td className="relative px-7 sm:w-12 sm:px-6">
                        {isSelected && (
                          <div className="absolute inset-y-0 left-0 w-0.5 bg-indigo-600"></div>
                        )}
                        <input
                          type="checkbox"
                          className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          value={user.email}
                          checked={isSelected}
                          onChange={(e) => onUserSelectionChange(user.email, e.target.checked)}
                        />
                      </td>
                      <td className="whitespace-nowrap py-4 pr-3 text-sm">
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-gray-500">{user.email}</div>
                      </td>
                      <td className={`whitespace-nowrap px-3 py-4 text-sm ${color}`}>
                        <div className="flex items-center">
                          {icon}
                          <span className="ml-2">{statusText}</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.managedByApp ? 
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">Oui</span> : 
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">Non</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 max-w-xs truncate" title={user.statusDetails?.message || 'N/A'}>
                        {user.statusDetails?.message || 'N/A'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {user.suspended ? 
                          <span className="inline-flex items-center text-red-600"><UserX size={16} className="mr-1"/> Suspendu</span> : 
                          <span className="inline-flex items-center text-green-600"><CheckCircle size={16} className="mr-1"/> Actif</span>}
                        <div className="text-xs text-gray-400">Créé: {user.creationTime ? new Date(user.creationTime).toLocaleDateString() : 'N/A'}</div>
                        <div className="text-xs text-gray-400">Dern. Connexion: {user.lastLoginTime ? new Date(user.lastLoginTime).toLocaleDateString() : 'N/A'}</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-center space-x-2">
                        {user.generatedSignatureHtml && !user.signatureConfigured && (
                          <button 
                            type="button"
                            onClick={() => onUseStoredSignature(user.generatedSignatureHtml)}
                            title="Utiliser la signature initiale générée pour cet utilisateur"
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-100"
                          >
                            <ClipboardPaste size={18} />
                          </button>
                        )}
                        {!user.suspended && onSuspendUser && (
                          <button
                            type="button"
                            onClick={() => onSuspendUser(user.email)}
                            title={`Suspendre l'utilisateur ${user.email}`}
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100"
                          >
                            <Ban size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserList; 